import { useEffect, useRef, useState } from "react";
import { useMaintenanceStory } from "../hooks";
import { MAX_TIMEOUT_DELAY_MS, computeIsInMaintenanceRange, parseLocalDateTime } from "../utils/maintenance";
import { LoadingOverlay } from "./LoadingOverlay";
import { SideContentLayout } from "./SideContentLayout";
import { Headline } from "./atoms/Headline";
import { Text } from "./atoms/Text";

export function MaintenanceGuard({ children }: Readonly<{ children: React.ReactNode }>) {
  const { maintenance, isLoadingMaintenance } = useMaintenanceStory();
  const [isCheckingForMaintenance, setIsCheckingForMaintenance] = useState(true);
  const [isInMaintenanceTimeRange, setIsInMaintenanceTimeRange] = useState(false);
  const timeoutsRef = useRef<{ start?: number; end?: number }>({});

  useEffect(() => {
    // always cleanup previous timers
    if (timeoutsRef.current.start) globalThis.clearTimeout(timeoutsRef.current.start);
    if (timeoutsRef.current.end) globalThis.clearTimeout(timeoutsRef.current.end);
    timeoutsRef.current = {};

    const cleanup = () => {
      if (timeoutsRef.current.start) globalThis.clearTimeout(timeoutsRef.current.start);
      if (timeoutsRef.current.end) globalThis.clearTimeout(timeoutsRef.current.end);
    };

    try {
      if (isLoadingMaintenance) {
        return cleanup;
      }

      if (!maintenance?.ReleaseDate || !maintenance?.ReleaseEnd || maintenance.Deployed) {
        setIsInMaintenanceTimeRange(false);
        setIsCheckingForMaintenance(false);
        return cleanup;
      }

      const startDate = parseLocalDateTime(maintenance.ReleaseDate);
      const endDate = parseLocalDateTime(maintenance.ReleaseEnd);
      if (!startDate || !endDate) {
        setIsInMaintenanceTimeRange(false);
        setIsCheckingForMaintenance(false);
        return cleanup;
      }

      const now = new Date();
      const msUntilStart = startDate.getTime() - now.getTime();
      const msUntilEnd = endDate.getTime() - now.getTime();

      const recheck = () => {
        try {
          setIsInMaintenanceTimeRange(computeIsInMaintenanceRange(maintenance));
        } catch {
          setIsInMaintenanceTimeRange(false);
        }
      };

      const schedule = (cb: () => void, delayMs: number): number | undefined => {
        if (delayMs <= 0) {
          cb();
          return undefined;
        }
        const safeDelay = Math.min(delayMs, MAX_TIMEOUT_DELAY_MS);
        return globalThis.setTimeout(cb, safeDelay) as unknown as number;
      };

      if (now < startDate) {
        timeoutsRef.current.start = schedule(recheck, msUntilStart);
        timeoutsRef.current.end = schedule(recheck, msUntilEnd);
      } else if (now >= startDate && now < endDate) {
        timeoutsRef.current.end = schedule(recheck, msUntilEnd);
      }

      // initial evaluation
      setIsInMaintenanceTimeRange(computeIsInMaintenanceRange(maintenance, now));
      setIsCheckingForMaintenance(false);
    } catch {
      setIsInMaintenanceTimeRange(false);
      setIsCheckingForMaintenance(false);
    }

    return cleanup;
  }, [maintenance, isLoadingMaintenance]);

  if (isCheckingForMaintenance) {
    return <LoadingOverlay />;
  }

  if (isInMaintenanceTimeRange && maintenance?.ReleaseDate && maintenance?.ReleaseEnd) {
    return (
      <SideContentLayout>
        <div className="bg-primary-100 my-2 flex h-18 items-center gap-4 rounded-full px-7 py-4">
          <div className="h-5 w-5 rounded-full bg-white" />
          <Text fontWeight="font-extrabold" className="mr-1 text-[20px] text-white">
            {maintenance.Tag}
          </Text>
        </div>
        <Headline fontWeight="font-bold" size="3xl">
          {maintenance.Title}
        </Headline>
        <Text className="text-lg leading-[32px] text-gray-800/70">{maintenance.Content}</Text>
      </SideContentLayout>
    );
  }

  return <>{children}</>;
}
